import {
  BrowserRouter as Router,
  Navigate,
  Route,
  Routes,
} from "react-router-dom";
import "./App.scss"; // App.scss must be imported before other components
import Reports from "pages/reports/Reports";
import LpiReports from "pages/lpiReports/LpiReports";
import CpiReports from "pages/cpiReports/CpiReports";
import LpiUploads from "pages/lpiUploads/LpiUploads";
import Overview from "pages/overview/Overview";
import Uploads from "pages/uploads/Uploads";
import Login from "pages/login/Login";
import useScrollbarWidth from "hooks/useScrollbarWidth";
import { ProtectedRoute } from "auth/protectedRoute";
import { SessionTimeout } from "auth/sessionTimeout";
import { RoleRefresh } from "auth/roleRefresh";
import SendCode from "pages/login/SendCode";
import ResetPassword from "pages/login/ResetPassword";
import Indicators from "pages/indicators/Indicators";
import EmissionFactors from "pages/emissionFactors/EmissionFactors";
import ReleaseNotesPage from "pages/releaseNotes/ReleaseNotesPage";
import MediaAdmin from "pages/mediaAdmin/MediaAdmin";
import AdminShell from "pages/admin/AdminShell";
import AdminDeletions from "pages/admin/AdminDeletions";
import AdminTenants from "pages/admin/AdminTenants";
import ReleaseNotesAdmin from "pages/releaseNotesAdmin/ReleaseNotesAdmin";
import NotFound from "pages/notFound/NotFound";
import NoAccess from "pages/noAccess/NoAccess";
import { LoginLayout } from "pages/login/LoginLayout";
import {
  EDITOR_ROLES,
  LPI_ROLES,
  ROLES,
  VIEWER_ROLES,
} from "auth/accountProvider";
import { AppLayout } from "pages/AppLayout";

const App = () => {
  // Publishes --scrollbar-width so the full-bleed filter bars can correct
  // their breakout; 100vw counts the scrollbar and the container does not.
  useScrollbarWidth();

  return (
  <Router>
    <div className="app">
      <SessionTimeout />
      {/*
        Re-checks roles on every route change, so a demotion in Zitadel takes
        effect without a logout. Sits inside the Router (it needs useLocation)
        and above <Routes> so it survives every route swap instead of
        remounting with one.
      */}
      <RoleRefresh />
      <Routes>
        <Route element={<LoginLayout />}>
          <Route path="/login" element={<Login />} />
          <Route path="/send-code" element={<SendCode />} />
          <Route path="/reset-password" element={<ResetPassword />} />
        </Route>
        <Route element={<AppLayout />}>
          {/*
            Terminal landing page for an authenticated user with no recognised
            role. It MUST stay outside every role-gated ProtectedRoute:
            ProtectedRoute redirects denials here, so gating it would make the
            redirect bounce off its own gate forever.
          */}
          <Route path="/no-access" element={<NoAccess />} />
          <Route element={<ProtectedRoute roles={VIEWER_ROLES} />}>
            <Route path="/" element={<Overview />} />
            <Route path="/reports" element={<Reports />} />
            <Route path="/cpi-reports" element={<CpiReports />} />
            <Route path="/indicators" element={<Indicators />} />
            <Route path="/emission-factors" element={<EmissionFactors />} />
            <Route path="/release-notes" element={<ReleaseNotesPage />} />
            <Route path="/*" element={<NotFound />} />
          </Route>
          {/* LPI is a purchased module: entitlement-gated, not level-gated.
              Hiding the nav entry alone would leave the page reachable by
              direct URL. */}
          <Route element={<ProtectedRoute roles={LPI_ROLES} />}>
            <Route path="/lpi-reports" element={<LpiReports />} />
          </Route>
          <Route element={<ProtectedRoute roles={EDITOR_ROLES} />}>
            <Route path="/data" element={<LpiUploads />} />
            <Route path="/upload" element={<Uploads />} />
          </Route>
          {/*
            super_admin-only admin area. Its tabs are routes rather than
            ?tab= states so /admin/media keeps the URL it has always had, and
            none of it is listed in the main nav (see MainMenu).
          */}
          <Route element={<ProtectedRoute roles={[ROLES.SUPER_ADMIN]} />}>
            <Route path="/admin" element={<AdminShell />}>
              <Route index element={<Navigate to="media" replace />} />
              <Route path="media" element={<MediaAdmin />} />
              <Route path="release-notes" element={<ReleaseNotesAdmin />} />
              <Route path="deletions" element={<AdminDeletions />} />
              <Route path="tenants" element={<AdminTenants />} />
            </Route>
          </Route>
        </Route>
      </Routes>

      {/* Cookie button must always stay mounted to work properly */}
      <button
        id="ot-sdk-btn"
        className="ot-sdk-show-settings"
        type="button"
        aria-label="Cookie Settings"
      />
    </div>
  </Router>
  );
};

export default App;
